import { Component, OnInit, HostListener, ViewContainerRef, TemplateRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationBackendService, BackendNotification } from '../../services/notification-backend.service';
import { AuthService } from '../../services/auth.service';
import { UserPermissions, userHasPermission } from '../../services/permissions';
import { OverlayModule, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'app-notification-center',
  standalone: true,
  imports: [CommonModule, TranslateModule, OverlayModule],
  templateUrl: './notification-center.component.html',
  styleUrl: './notification-center.component.scss'
})
export class NotificationCenterComponent implements OnInit {
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;

  // Il pannello di invio è ospitato dalla sidebar (che gestisce anche lo z-index
  // dell'aside mentre è aperto): qui ci limitiamo a chiederne l'apertura.
  @Output() newNotification = new EventEmitter<void>();

  notifications$: Observable<BackendNotification[]>;
  unreadCount$: Observable<number>;
  public isOpen = false; // Public so sidebar can check it
  private overlayRef: OverlayRef | null = null;

  constructor(
    private notificationBackendService: NotificationBackendService,
    private router: Router,
    private authService: AuthService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    this.notifications$ = this.notificationBackendService.notifications$;
    this.unreadCount$ = this.notificationBackendService.unreadCount$;
  }

  ngOnInit(): void {
    // Notifications are loaded automatically by the service
  }

  // Stesso gate del pannello di invio: senza il permesso l'header resta identico
  // a prima (nessuna "+"), così non si crea uno spazio vuoto asimmetrico.
  get canBroadcastNotifications(): boolean {
    return userHasPermission(
      this.authService.currentUserValue?.permissions,
      UserPermissions.SPECIAL_FEATURES_CAN_BROADCAST
    );
  }

  openNewNotification(event: Event): void {
    event.stopPropagation();
    this.closeDropdown();
    this.newNotification.emit();
  }

  toggleDropdown(): void {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  private openDropdown(): void {
    this.isOpen = true;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay.position()
        .global()
        .left('260px')
        .bottom('8rem');

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.reposition()
      });
    }

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  closeDropdown(): void {
    this.isOpen = false;
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInBell = target.closest('.notification-bell');
    const clickedInDropdown = target.closest('.notification-dropdown');

    if (!clickedInBell && !clickedInDropdown && this.isOpen) {
      this.closeDropdown();
    }
  }

  trackById(_index: number, notification: BackendNotification): string {
    return notification.ID;
  }

  markAsRead(notification: BackendNotification, event: Event): void {
    event.stopPropagation();
    if (!notification.Managed) {
      this.notificationBackendService.setReadStateAndUpdate([notification.ID], true);
    }
  }

  navigateToNotification(notification: BackendNotification): void {
    // Mark as read
    if (!notification.Managed) {
      this.notificationBackendService.setReadStateAndUpdate([notification.ID], true);
    }

    // Navigate to referral if exists. Il pannello si chiude SOLO quando si naviga
    // davvero: così marcare come lette più notifiche (es. broadcast senza referral)
    // non costringe a riaprire il pannello ogni volta.
    if (notification.Refferal) {
      if (notification.Refferal === 'audiences' || notification.Refferal.startsWith('audiences/')) {
        this.router.navigate(['/reach/audiences']);
      } else {
        this.router.navigate([`/${notification.Refferal}`]);
      }
      this.closeDropdown();
    }
  }

  getNotificationTypeClass(type: string): string {
    return `notification-item--${type}`;
  }

  viewAll(): void {
    this.closeDropdown();
    this.router.navigate(['/notifications']);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}
